import {createSnippet, dbUtil, decorateSnapshot} from '@/api/firebase';
import Defer from '@/utils/defer';
import {valueDiff} from '@/utils/diff';
import {byRef} from '@/utils/firestore';
import {Logger} from '@vanti/vue-logger';
import Vue from 'vue';

const log = Logger.get('resources');

export default {
  namespaced: true,
  state: {
    /**
     * All the resources we know about and have fetched from the remote db.
     * Keyed by ref
     */
    byRef: {}
  },
  getters: {
    allParentRefs(state) {
      const parentRefs = {};
      for (const b of Object.values(state.byRef)) {
        if (Array.isArray(b.parents)) {
          for (const parent of b.parents) {
            parentRefs[byRef(parent)] = parent.ref;
          }
        }
      }
      return Object.values(parentRefs);
    },
    knownResources(state) {
      return Object.values(state.byRef);
    },
    resourceByRef(state) {
      return ref => state.byRef[ref];
    }
  },
  mutations: {
    ...Defer.mutations(log),
    setResource(state, resource) {
      Vue.set(state.byRef, byRef(resource), resource);
    },
    deleteResource(state, ref) {
      Vue.delete(state.byRef, ref);
    }
  },
  actions: {
    init: {
      root: true,
      handler({getters, dispatch}) {
        // watch the active resource parents so we can fetch all those resources too
        this.watch(
            () => getters['allParentRefs'],
            (parents, oldParents) => {
              const {added, removed} = valueDiff(oldParents, parents, p => p.path);
              added.forEach(ref => dispatch('watchResource', ref));
              removed.forEach(ref => dispatch('forgetResource', ref));
            },
            {immediate: true}
        );
      }
    },
    watchResource({commit}, ref) {
      commit('defer', {
        [ref.path]: ref.onSnapshot(
            snap => commit('setResource', decorateSnapshot(snap)),
            err => log.error(`watchResource ${ref.path}.onSnapshot`, err)
        )
      });
    },
    forgetResource({commit}, ref) {
      commit('reset', [ref.path]);
      commit('deleteResource', ref.path);
    }
  },
  modules: {
    /**
     * The active module represent the chosen resource for the booking panel.
     * It is responsible for fetching the data associated with the resource.
     * State is stored in the enclosing resources module.
     */
    active: {
      namespaced: true,
      getters: {
        resource(state, getters, rootState, rootGetters) {
          const resourceRef = rootGetters['config/resourceRef'];
          if (!resourceRef) return null;
          return rootGetters['resources/resourceByRef'](resourceRef.path);
        },
        resourcePath(state, getters) {
          const resource = getters.resource;
          return resource && resource.ref.path;
        },
        title(state, getters) {
          if (!getters.resource) return '';
          return getters.resource.title || getters.resource.id || '';
        },
        snippet(state, getters) {
          if (!getters.resource) return null;
          return createSnippet(getters.resource);
        },
        parents(state, getters) {
          if (!getters.resource) return [];
          return (getters.resource.parents || []).map(p => createSnippet(p));
        },
        refsContributeBookings(state, getters, rootState, rootGetters) {
          return rootGetters['resources/knownResources']
              .map(b => b.ref);
        }
      },
      mutations: {
        ...Defer.mutations(log)
      },
      actions: {
        init: {
          root: true,
          handler({dispatch, rootGetters}) {
            this.watch(
                () => rootGetters['config/resourceRef'],
                () => dispatch('watchActiveResource'),
                {immediate: true}
            );
          }
        },

        async watchActiveResource({commit, rootGetters}) {
          let resourceRef = rootGetters['config/resourceRef'];
          if (!resourceRef) {
            commit('reset');
            commit('resources/deleteResource', null, {root: true});
            return;
          }

          if (typeof resourceRef === 'string') {
            resourceRef = await dbUtil.doc(resourceRef);
          }

          commit('defer', {
            remoteResource: resourceRef.onSnapshot(
                snap => commit('resources/setResource', decorateSnapshot(snap), {root: true}),
                err => log.error(`active.watchActiveResource ${resourceRef.path}.onSnapshot`, err))
          });
        }
      }
    }
  }
};
